import React, { useState, useEffect } from 'react';
import ReactGA from 'react-ga4';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import logo from './blik-logo.png'
import { withTranslation } from 'react-i18next';
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import Grid from "@material-ui/core/Grid";
import ConfirmPaymentDialog from './ConfirmPaymentDialog';

const styles = {
  image: {
    width: '100%',
    maxWidth: '100px'
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column'
  }
}

const BlikForm = ({ classes, t, paymentStatus: propPaymentStatus, disabled, validateEmail, scrollToEmail, updateEmail, submitUrl, onPaymentCompleted }) => {
  const [valid, setValid] = useState(false);
  const [code, setCode] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(propPaymentStatus);

  // Update local paymentStatus when prop changes
  useEffect(() => {
    setPaymentStatus(propPaymentStatus);
  }, [propPaymentStatus]);

  // Open dialog when paymentStatus is awaiting_confirmation (e.g., after page refresh)
  useEffect(() => {
    if (paymentStatus === 'awaiting_confirmation' || paymentStatus === 'failed') {
      setOpenDialog(true);
    }
  }, [paymentStatus]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const statusMsg = (t) => {
    if (paymentStatus === 'awaiting_confirmation') {
      return t('blikPaymentConfirm');
    } else if (paymentStatus === 'failed') {
      return t('blikPaymentFailed');
    } else if (paymentStatus === 'processing') {
      return t('otherPaymentProcessing');
    } else {
      return t('blikPaymentGenerateCode');
    }
  };

  const inputDisabled = () => {
    return disabled || paymentStatus === 'processing' || paymentStatus === 'awaiting_confirmation';
  };

  const handleChange = e => {
    const value = e.target.value.replace(/\D/g, '').substr(0, 6);
    const isValid = value.length === 6;
    setValid(isValid);
    setCode(value);
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!valid) {
      return;
    }

    // Check if emailInput is valid
    if (!validateEmail()) {
      scrollToEmail();
      return;
    }

    ReactGA.event({
      category: 'Payment',
      action: 'started',
      label: 'blik'
    });

    setOpenDialog(true);
    setPaymentStatus('awaiting_confirmation');

    // Update email before payment
    updateEmail(() => {
      fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider: 'blik',
          blik_code: code,
        })
      }).then(function(response) {
        return response.json();
      }).then(function(json) {
        ReactGA.event({
          category: 'Payment',
          action: 'completed',
          label: 'blik'
        });
        onPaymentCompleted(json);
        if (["completed", "failed"].includes(json.payment.status)) {
          setOpenDialog(false);
          setCode('');
        }
      });
    });
  };

  return (
    <>
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2} justifyContent="space-between" alignItems="center" wrap="nowrap">
        <Grid item sm={4} xs={2}>
          <img src={logo} alt="blik-logo" className={classes.image} />
        </Grid>
        <Grid item xs={5} sm={4} container justifyContent="flex-end">
          <div className={classes.inputContainer}>
            <TextField
              label={t('blikCode')}
              className={classes.input}
              onChange={handleChange}
              value={code}
              type="tel"
              error={paymentStatus === 'failed' || (code !== '' && !valid)}
              disabled={inputDisabled()}
            />
            <Typography variant="caption" className={classes.caption} si>
              { statusMsg(t) }
            </Typography>
          </div>
        </Grid>
        <Grid item xs={5} sm={4} container justifyContent="flex-end">
          <Button
            variant="contained"
            type="submit"
            color="primary"
            disabled={!valid || inputDisabled()}>
              { paymentStatus === 'awaiting_confirmation' ?
                <CircularProgress size={14} />
                : t('buyAndPay')
              }
          </Button>
        </Grid>
      </Grid>
    </form>
    <ConfirmPaymentDialog
      paymentStatus={paymentStatus}
      open={openDialog}
      onClose={handleCloseDialog}
      handleCloseDialog={handleCloseDialog}/>
    </>
  );
};

export default withStyles(styles)(withTranslation()(BlikForm));
